/**
 * Draft Store
 * 
 * Zustand store for managing draft changes in localStorage
 * Drafts are stored per user, per clinic in browser localStorage
 * 
 * Key Format: `drafts_${clinicId}_${userId}`
 * 
 * Each draft change has:
 * - id: unique identifier
 * - resource_type: 'drug_program' | 'pharmacy' | 'physician' | 'program_category'
 *                | 'item_family' | 'item' | 'item_price' | 'coupon'
 * - resource_id: ID of resource (null for creates)
 * - operation: 'create' | 'update' | 'delete'  (local resources)
 *            | 'create' | 'update' | 'archive' | 'unarchive'  (pricing)
 *
 * The four pricing resource types live in Manifold rather than the clinic
 * database. They never carry 'delete': a price a paid invoice references has
 * to stay resolvable, so they are archived instead.
 * - original_data: Original data before change (for UI diff)
 * - new_data: New/updated data
 * - timestamp: When change was made
 * - description: Optional user-provided description
 */

import { create } from 'zustand'

// Generate UUID using browser's crypto API
const generateId = () => {
  return crypto.randomUUID()
}

/**
 * A draft store. The app has two: the clinic store (default export), whose
 * namespace follows the selected clinic, and the shared doctor directory's,
 * which belongs to no clinic. Separate instances keep shared-directory drafts
 * out of the clinic's publish flow and header indicator.
 */
export const createDraftStore = () => create((set, get) => ({
  changes: [],
  clinicId: null,
  userId: null,
  isInitialized: false,

  /**
   * Initialize the store and load drafts from localStorage
   */
  initialize: (clinicId, userId) => {
    const storageKey = `drafts_${clinicId}_${userId}`
    
    try {
      const stored = localStorage.getItem(storageKey)
      const changes = stored ? JSON.parse(stored).changes || [] : []
      
      set({ 
        changes, 
        clinicId, 
        userId, 
        isInitialized: true 
      })
      
      console.log(`[DRAFTS] Initialized with ${changes.length} draft changes (key: ${storageKey})`)
    } catch (error) {
      console.error('[DRAFTS] Error loading from localStorage:', error)
      set({ 
        changes: [], 
        clinicId, 
        userId, 
        isInitialized: true 
      })
    }
  },

  /**
   * Save current state to localStorage
   */
  _saveToLocalStorage: () => {
    const { changes, clinicId, userId } = get()
    
    if (!clinicId || !userId) {
      console.warn('[DRAFTS] Cannot save - clinic or user not initialized')
      return
    }
    
    const storageKey = `drafts_${clinicId}_${userId}`
    const data = {
      changes,
      last_modified: Date.now()
    }
    
    try {
      localStorage.setItem(storageKey, JSON.stringify(data))
    } catch (error) {
      console.error('[DRAFTS] Error saving to localStorage:', error)
    }
  },

  /**
   * Add a new change to drafts
   */
  addChange: (change) => {
    const newChange = {
      id: change.id || generateId(),
      resource_type: change.resource_type,
      resource_id: change.resource_id || null,
      operation: change.operation,
      original_data: change.original_data || null,
      new_data: change.new_data,
      timestamp: Date.now(),
      description: change.description || null
    }
    
    set(state => ({ 
      changes: [...state.changes, newChange] 
    }))
    
    get()._saveToLocalStorage()
    console.log('[DRAFTS] Added change:', newChange.id, newChange.operation, newChange.resource_type)
  },

  /**
   * Update an existing change
   */
  updateChange: (id, updates) => {
    set(state => ({
      changes: state.changes.map(change => 
        change.id === id 
          ? { ...change, ...updates, timestamp: Date.now() }
          : change
      )
    }))
    
    get()._saveToLocalStorage()
    console.log('[DRAFTS] Updated change:', id)
  },

  /**
   * Remove a specific change
   */
  removeChange: (id) => {
    set(state => ({
      changes: state.changes.filter(change => change.id !== id)
    }))
    
    get()._saveToLocalStorage()
    console.log('[DRAFTS] Removed change:', id)
  },

  /**
   * Remove all changes
   */
  clearDrafts: () => {
    set({ changes: [] })
    get()._saveToLocalStorage()
    console.log('[DRAFTS] Cleared all drafts')
  },

  /**
   * Get count of pending changes
   */
  getCount: () => {
    return get().changes.length
  },

  /**
   * Check if there are unsaved changes
   */
  hasUnsaved: () => {
    return get().changes.length > 0
  },

  /**
   * Get changes by resource type
   */
  getChangesByType: (resourceType) => {
    return get().changes.filter(c => c.resource_type === resourceType)
  },

  /**
   * Get changes by operation
   */
  getChangesByOperation: (operation) => {
    return get().changes.filter(c => c.operation === operation)
  },

  /**
   * Get change summary for UI display
   */
  getChangeSummary: () => {
    const { changes } = get()
    
    const countOp = (op) => changes.filter(c => c.operation === op).length
    const countType = (type) => changes.filter(c => c.resource_type === type).length

    const summary = {
      total: changes.length,
      creates: countOp('create'),
      updates: countOp('update'),
      deletes: countOp('delete'),
      // Pricing resources archive instead of deleting
      archives: countOp('archive'),
      unarchives: countOp('unarchive'),
      by_type: {
        drug_program: countType('drug_program'),
        pharmacy: countType('pharmacy'),
        physician: countType('physician'),
        program_category: countType('program_category'),
        item_family: countType('item_family'),
        item: countType('item'),
        item_price: countType('item_price'),
        coupon: countType('coupon')
      }
    }

    return summary
  },

  /**
   * Get all changes formatted for API submission
   */
  getChangesForPublish: () => {
    const { changes } = get()
    
    return changes.map(change => ({
      resource_type: change.resource_type,
      resource_id: change.resource_id,
      operation: change.operation,
      data: change.new_data
    }))
  },

  /**
   * Find if a resource already has a pending change
   */
  findChangeForResource: (resourceType, resourceId) => {
    return get().changes.find(
      c => c.resource_type === resourceType && c.resource_id === resourceId
    )
  }
}))

const useDraftStore = createDraftStore()

/** Drafts for core's shared doctor directory; initialize with SHARED_DIRECTORY_SCOPE. */
export const useSharedDraftStore = createDraftStore()
export const SHARED_DIRECTORY_SCOPE = 'shared_physicians'

export default useDraftStore

